import { apiClient } from './apiClient';

export type State = {
  id: number;
  name: string;
  postalCode: string;
  parentStateId: number;
  parentState: {
    id: number;
    name: string;
  };
  stateType: {
    id: number;
    name: string;
  };
};

export const StateTypeIds = {
  Nation: 1,
  State: 2,
  City: 3,
} as const;

export type StateType = {
  id: number;
  name: string;
  allowedParentId: number | null;
};

export type StateResponse = {
  results: State[];
  totalCount: number;
};

export type ValidateStatesPayload = {
  dateEstablished?: string;
  closeDate?: string;
  states: {
    id: number;
    startingDate?: string;
    endingDate?: string;
    description: string;
    residenceId: number;
    governmentId: string;
  }[];
};

export function getStateTypes() {
  return apiClient.get<{
    results: StateType[];
  }>(`states/state-types`, {});
}

export type StatesParams = {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortType?: string;
  excludeCities?: boolean;
  excludeNations?: boolean;
};

export const getStates = (params?: StatesParams): Promise<StateResponse> => {
  return apiClient.get(`/states`, { ...params });
};

export const getStatesInUse = (
  params?: StatesParams
): Promise<StateResponse> => {
  return apiClient.get(`/states/in-use`, { ...params });
};

export function addState(
  name: State['name'],
  postalCode: State['postalCode'],
  stateType: StateType,
  parent?: State
) {
  return apiClient.post('states', {
    name,
    postalCode,
    stateTypeId: stateType.id,
    parentStateId: parent?.id,
  });
}

export function editState(
  id: State['id'],
  postalCode: State['postalCode'],
  name: State['name'],
  stateTypeId?: State['stateType']['id'],
  parentStateId?: State['parentStateId']
) {
  return apiClient.put(`/states/${id}`, {
    postalCode,
    name,
    stateTypeId,
    parentStateId,
  });
}

export function deleteState(state: State) {
  return apiClient.del(`states/${state.id}`);
}

export function validateCreateState(
  name: State['name'],
  postalCode: State['postalCode'],
  stateType: StateType,
  parent?: State
) {
  return apiClient.post('states/validate-create', {
    name,
    postalCode,
    stateTypeId: stateType.id,
    parentStateId: parent?.id,
  });
}

export function validateUpdateState(
  id: State['id'],
  postalCode: State['postalCode'],
  name: State['name'],
  stateTypeId?: State['stateType']['id'],
  parentStateId?: State['parentStateId']
) {
  return apiClient.post(`/states/${id}/validate-update`, {
    postalCode,
    name,
    stateTypeId,
    parentStateId,
  });
}

export async function isStateInUse(state: State) {
  const { isInUse } = await apiClient.get<{
    isInUse: boolean;
  }>(`states/${state.id}`, {});
  return isInUse;
}

export type StateResidence = {
  id: number;
  name: string;
};

export type StateResidecesResponse = {
  results: StateResidence[];
};

export const getResidences = (): Promise<StateResidecesResponse> => {
  return apiClient.get('states/state-residences', {});
};

export const validateStatesHistory = (
  clientId: number,
  payload: ValidateStatesPayload
): Promise<{ isValid: boolean; validationErrors?: string[] }> => {
  return apiClient.post(`clients/${clientId}/validate-states-history`, payload);
};
